
import React from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  AlertCircle, 
  ClipboardList, 
  Users, 
  Trophy,
  Clock,
  Shield,
  FileDown,
  Filter,
  RefreshCw
} from 'lucide-react';

const AdminDocumentationPage: React.FC = () => {
  return (
    <AdminLayout title="Admin Documentation">
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="changelogs">Change Logs</TabsTrigger>
          <TabsTrigger value="users">User Management</TabsTrigger>
          <TabsTrigger value="plans">Permanent Plans</TabsTrigger>
          <TabsTrigger value="widgets">Widget Status</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Admin Dashboard Guide</CardTitle>
              <CardDescription>
                Overview of administrator privileges and capabilities
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                As an administrator, you have privileged access to monitoring and management features 
                that help maintain the platform. This documentation provides guidance on using these tools 
                effectively.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="flex items-start space-x-3">
                  <Shield className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div>
                    <h3 className="font-medium">Admin Privileges</h3>
                    <p className="text-sm text-muted-foreground">
                      Administrators can access user data, manage permanent plans, view system logs, 
                      and monitor widget performance across all installations.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Clock className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div>
                    <h3 className="font-medium">Real-time Monitoring</h3>
                    <p className="text-sm text-muted-foreground">
                      View real-time status of widget installations, track changes across the system,
                      and ensure smooth operation of all platform components.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Security Responsibilities</CardTitle>
              <CardDescription>
                Important security considerations for administrators
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                With great power comes great responsibility. As an administrator, you have access to sensitive user data
                and system information. Please follow these security guidelines:
              </p>
              
              <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground">
                <li>Never share your admin credentials with others</li>
                <li>Log out when not actively using the admin dashboard</li>
                <li>Be careful when exporting sensitive data</li>
                <li>Regularly review the change logs for suspicious activity</li>
                <li>Report any security concerns immediately</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="changelogs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Change Logs Interpretation Guide</CardTitle>
              <CardDescription>
                Understanding and using the change logs system
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                The change logs track all significant modifications to the system, providing an audit trail
                and helping diagnose issues. This section explains how to interpret and use these logs effectively.
              </p>
              
              <h3 className="font-medium mt-4">Log Components</h3>
              <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground">
                <li><strong>Component Type:</strong> Categorizes the change (Widget, Website, User, etc.)</li>
                <li><strong>Component Name:</strong> Specific component affected</li>
                <li><strong>Action:</strong> Type of change (create, update, delete)</li>
                <li><strong>Description:</strong> Detailed explanation of the change</li>
                <li><strong>User:</strong> Who made the change</li>
                <li><strong>Timestamp:</strong> When the change occurred</li>
              </ul>
              
              <h3 className="font-medium mt-4">Filtering and Exporting</h3>
              <div className="flex items-start space-x-3 mt-2">
                <Filter className="h-5 w-5 text-blue-500 mt-0.5" />
                <div>
                  <h4 className="font-medium">Filtering Logs</h4>
                  <p className="text-sm text-muted-foreground">
                    Use the filter options to narrow down logs by component type, action, date range, or user.
                    This helps when investigating specific issues or changes.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 mt-2">
                <FileDown className="h-5 w-5 text-blue-500 mt-0.5" />
                <div>
                  <h4 className="font-medium">Exporting Data</h4>
                  <p className="text-sm text-muted-foreground">
                    Export logs as CSV for further analysis or reporting. The export includes all fields and
                    respects any filters you've applied.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">User Management Guide</CardTitle>
              <CardDescription>
                Managing administrators and user roles
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p>
                This section covers how to view and manage user information, particularly focusing on
                administrative users and their privileges.
              </p>
              
              <h3 className="font-medium mt-4">Viewing Admin Users</h3>
              <p className="text-sm text-muted-foreground">
                The Admin Users page displays all users with administrative privileges, including their role,
                subscription status, and when they were granted admin access.
              </p>
              
              <h3 className="font-medium mt-4">User Roles System</h3>
              <p className="text-sm text-muted-foreground">
                The platform uses a role-based access control system with the following roles:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground mt-2">
                <li><strong>Admin:</strong> Full access to all administrative functions</li>
                <li><strong>User:</strong> Standard user permissions</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="plans" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Permanent Pro Plan Guide</CardTitle>
              <CardDescription>
                Managing lifetime access subscriptions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <h3 className="font-medium">Understanding Permanent Pro Plans</h3>
              <p className="text-sm text-muted-foreground">
                Permanent Pro Plans provide lifetime access to premium features without recurring payments.
                These are special subscriptions that never expire and maintain all Pro features.
              </p>
              
              <h3 className="font-medium mt-4">Assigning Permanent Pro Access</h3>
              <p className="text-sm text-muted-foreground">
                To assign permanent access:
              </p>
              <ol className="list-decimal pl-5 space-y-2 text-sm text-muted-foreground mt-2">
                <li>Use the search field to find a user by email</li>
                <li>Click "Assign Permanent Pro" once the user is found</li>
                <li>The system will either create a new permanent subscription or convert an existing one</li>
              </ol>
              
              <h3 className="font-medium mt-4">Permanent Plan Indicators</h3>
              <p className="text-sm text-muted-foreground">
                Throughout the admin interface, permanent plans are indicated by the Trophy (🏆) icon
                next to the subscription status. This helps quickly identify users with lifetime access.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="widgets" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Widget Status Monitoring</CardTitle>
              <CardDescription>
                Tracking widget installations across domains
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p>
                The Widget Status dashboard shows real-time information about all widget installations
                across customer websites.
              </p>
              
              <h3 className="font-medium mt-4">Status Indicators</h3>
              <div className="mt-2 space-y-2">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                  <span className="text-sm"><strong>Online:</strong> Widget has pinged the server within the last 2 minutes</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                  <span className="text-sm"><strong>Offline:</strong> No recent ping from the widget</span>
                </div>
              </div>
              
              <h3 className="font-medium mt-4">Installation Details</h3>
              <p className="text-sm text-muted-foreground">
                For each installation, you can view:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground mt-2">
                <li>Domain where the widget is installed</li>
                <li>Installation key used to identify the widget</li>
                <li>Current status (online/offline)</li>
                <li>Last ping time (when the widget last communicated with the server)</li>
                <li>Widget version running on the domain</li>
              </ul>
              
              <p className="text-sm text-muted-foreground mt-4">
                This information helps diagnose issues with specific installations and understand the 
                overall health of the widget ecosystem.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default AdminDocumentationPage;
